import React, { useMemo } from 'react';
import { useLocation } from "react-router";
import { useIntl } from "react-intl";
import { matchPath, useNavigate } from "react-router-dom";
import classNames from "classnames";
import { checkPermission, VIEW_PERMISSION_MAP } from '@/util/permissionUtil';
import { usePermissions } from '@/query/permission';
import { useAgentId, useModal } from "@/hook";
import Button from "@/component/Button";
import AccountPopover from "@/component/AccountPopover";
import JobMonitorModal, { JOB_MONITOR_MODAL_ID, JobStatusMark } from "@/component/JobMonitorModal";
import AppMenu from '@/component/AppMenu';
import {
    AutocompleteIcon, BulkTestIcon, DashboardIcon, DeploymentIcon,
    EntityIcon, IntentIcon, MemberIcon, SettingIcon
} from "@/assets/svgs/icons";

import AgentDropdown from "./AgentDropdown";


type SubMenuContent = {
    label: string,
    to: string,
    permissions?: readonly string[]
}

type MenuContent = {
    icon: React.FC<{ className?: string }>,
    label: string,
    to: string,
    submenu?: SubMenuContent[],
    permissions?: readonly string[]
}

const AgentContextSideMenu: React.FC = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const agentId = useAgentId();
    const modal = useModal();

    const { data: userPermissions } = usePermissions(agentId);

    const onClickLogo = () => {
        navigate('/');
    };

    const onClickBGJBtn = () => {
        modal.open(JOB_MONITOR_MODAL_ID);
    };

    const menuContents = useMemo<MenuContent[]>(() => [
        {
            icon: DashboardIcon,
            label: intl.formatMessage({ id: "m02" }),
            to: `/agents/${agentId}/dashboard`,
            permissions: VIEW_PERMISSION_MAP.DASHBOARD
        }, {
            icon: IntentIcon,
            label: intl.formatMessage({ id: "m03" }),
            to: `/agents/${agentId}/intent`,
            submenu: [
                {
                    label: intl.formatMessage({ id: "m03" }),
                    to: `/agents/${agentId}/intent`,
                    permissions: VIEW_PERMISSION_MAP.INTENT
                }, {
                    label: intl.formatMessage({ id: "m04" }),
                    to: `/agents/${agentId}/selector`,
                    permissions: VIEW_PERMISSION_MAP.SELECTOR
                }
            ]
        }, {
            icon: EntityIcon,
            label: intl.formatMessage({ id: "m05" }),
            to: `/agents/${agentId}/entity`,
            submenu: [
                {
                    label: intl.formatMessage({ id: "m05" }),
                    to: `/agents/${agentId}/entity`,
                    permissions: VIEW_PERMISSION_MAP.ENTITY
                }, {
                    label: intl.formatMessage({ id: "m06" }),
                    to: `/agents/${agentId}/synonym`,
                    permissions: VIEW_PERMISSION_MAP.SYNONYM
                }, {
                    label: intl.formatMessage({ id: "m07" }),
                    to: `/agents/${agentId}/typo`,
                    permissions: VIEW_PERMISSION_MAP.TYPOS
                }
            ]
        }, {
            icon: AutocompleteIcon,
            label: intl.formatMessage({ id: "m08" }),
            to: `/agents/${agentId}/autocomplete`,
            permissions: VIEW_PERMISSION_MAP.AUTOCOMPLETE
        }, {
            icon: BulkTestIcon,
            label: intl.formatMessage({ id: "m09" }),
            to: `/agents/${agentId}/test`,
            permissions: VIEW_PERMISSION_MAP.BULK_TEST
        }, {
            icon: DeploymentIcon,
            label: intl.formatMessage({ id: "m10" }),
            to: `/agents/${agentId}/deploy`,
            permissions: VIEW_PERMISSION_MAP.DEPLOYMENT
        }, {
            icon: SettingIcon,
            label: intl.formatMessage({ id: "m13" }),
            to: `/agents/${agentId}/setting`,
            permissions: VIEW_PERMISSION_MAP.SETTING
        }, {
            icon: MemberIcon,
            label: intl.formatMessage({ id: "m12" }),
            to: `/agents/${agentId}/member`,
            permissions: VIEW_PERMISSION_MAP.MEMBER
        }
    ], [intl, agentId]);

    const filteredMenuContents = useMemo<MenuContent[]>(() => {
        if (!userPermissions) {
            return [];
        }
        return menuContents
            .filter(menu => {
                const viewPermissions = [
                    ...(menu.permissions ?? []),
                    ...(menu.submenu?.flatMap(sub => sub.permissions ?? []) ?? [])
                ];
                return checkPermission(userPermissions, viewPermissions);
            })
            .map(menu => {
                if (!menu.submenu) {
                    return menu;
                }
                return {
                    ...menu,
                    submenu: menu.submenu.filter(sub => checkPermission(userPermissions, sub.permissions ?? []))
                };
            });
    }, [menuContents, userPermissions]);

    if (!userPermissions) {
        return null;
    }
    return (<>
        <JobMonitorModal />

        <div className={classNames(
                 "w-[250px] h-full flex-none pt-[27px] pb-4.5 flex flex-col",
                 "bg-white border-r border-r-black-200 rounded-r-[20px] z-50"
             )}
        >
            <div className="flex gap-1 mb-5.5 mx-5 rounded-[6px]">
                <AppMenu className="text-chat42 hover:text-chat42-50 hover:bg-chat42-10" />
                <div className="w-full p-1 flex flex-col gap-0.5 cursor-pointer"
                     onClick={onClickLogo}
                >
                    <div className="text-chat42 text-[14px] font-[600] leading-none group-hover:text-chat42-50">
                        Chat42
                    </div>
                    <div className="text-chat42 text-[17px] font-[700] leading-none group-hover:text-chat42-50">
                        NLU
                    </div>
                </div>
            </div>

            <div className="mb-4.5 px-5">
                <AgentDropdown agentId={agentId} />
            </div>

            <div className="h-full flex flex-col items-start gap-1.5 px-5 scroll-hover-container">
                {filteredMenuContents.map(menu => (
                    <MenuFragment key={menu.label}
                                    menu={menu}
                    />
                ))}
            </div>

            <div className="flex flex-col gap-2 mt-3 px-5">
                <Button icon size="full"
                        className={classNames(
                            "justify-between p-[12px_15px] rounded-[10px] border border-black-200 bg-white",
                            "text-[14px] font-[400] leading-none text-black-400",
                            "hover:bg-black-100 hover:text-black-500 active:text-chat42",
                            modal.isOpen(JOB_MONITOR_MODAL_ID) && "bg-chat42-10 border-chat42-10 text-chat42 font-[500]"
                        )}
                        onClick={onClickBGJBtn}
                >
                    <div className="w-full flex justify-between items-center">
                        {intl.formatMessage({ id: 'i00002' })}
                        <JobStatusMark />
                    </div>
                </Button>

                <AccountPopover className="ml-2"
                                anchor="right end"
                                agentId={agentId}
                />
            </div>
        </div>
    </>);
};


const MenuFragment: React.FC<{ menu: MenuContent }> = ({ menu }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const isMatch = [
        menu.to,
        ...(menu.submenu?.map(sub => sub.to) ?? [])
    ].some(path => matchPath(`${path}/*`, location.pathname));

    const disabledMainMenu = menu.submenu && !menu.submenu.some(sub => sub.to === menu.to);

    const onClick = (e: React.MouseEvent, to: string) => {
        if (disabledMainMenu) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        navigate(to, { state: { from: "sidemenu" } });
    };

    const onAuxClick = (e: React.MouseEvent, to: string) => {
        if (disabledMainMenu && to === menu.to) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        window.open(to, "_blank");
    };

    return (
        <div className="w-full flex flex-col">
            <Button className={classNames(
                        "p-2.5 justify-start gap-x-2.5 rounded-[6px]",
                        disabledMainMenu && "pointer-events-none",
                        isMatch
                            ? "bg-chat42-10 text-chat42"
                            : "text-black-400"
                    )}
                    onClick={e => onClick(e, menu.to)}
                    onAuxClick={e => onAuxClick(e, menu.to)}
            >
                <menu.icon className="size-6" />
                <div className="font-[700] text-[16px] leading-[1.5]">
                    {menu.label}
                </div>
            </Button>
            {menu.submenu?.map(sub => (
                <Button key={sub.label}
                        className="p-2 justify-start pl-[44px] rounded-[6px]"
                        onClick={e => onClick(e, sub.to)}
                        onAuxClick={e => onAuxClick(e, sub.to)}
                >
                    <div className={classNames(
                             "text-[14px] leading-[1.5]",
                             matchPath({ path: sub.to, end: false }, location.pathname)
                                 ? "font-[600] text-chat42"
                                 : "font-[500] text-black-400"
                         )}
                    >
                        {sub.label}
                    </div>
                </Button>
            ))}
        </div>
    );
};

export default AgentContextSideMenu;